import React, {useCallback, useState} from 'react';
import {ConfirmationModal} from '@albert-io/atomic';

import {useDeleteDiagnostic} from './useDeleteDiagnostic';

interface DeleteDiagnosticModalProps {
  diagnosticId: string;
  onClose: () => void;
  /** Runs only when the delete succeeded, for callers that navigate away from the deleted record. */
  onDeleted?: () => void;
}

/** Confirms deleting an assigned diagnostic, shared by the dashboard row menu and the assign page. */
export const DeleteDiagnosticModal = ({
  diagnosticId,
  onClose,
  onDeleted
}: DeleteDiagnosticModalProps) => {
  const deleteDiagnostic = useDeleteDiagnostic();
  const [isDeleting, setIsDeleting] = useState(false);

  // Staying open until the request settles is the double-submit guard: the confirm button holds,
  // and the modal's own backdrop covers the trigger that opened it.
  const handleConfirm = useCallback(async () => {
    setIsDeleting(true);
    const deleted = await deleteDiagnostic(diagnosticId);
    onClose();
    if (deleted) onDeleted?.();
  }, [deleteDiagnostic, diagnosticId, onClose, onDeleted]);

  return (
    <ConfirmationModal
      badgeIcon={['fal', 'trash-alt']}
      badgeColor='primary'
      title='Delete diagnostic?'
      body='This action cannot be undone.'
      cancelText='Keep'
      confirmText={isDeleting ? 'Deleting…' : 'Delete'}
      confirmBtnColor='negative'
      confirmBtnVariant='solid'
      confirmDisabled={isDeleting}
      onCancel={onClose}
      onConfirm={handleConfirm}
    />
  );
};
